import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '../../i18n/hooks';
import { useCalculator } from '../context/CalculatorContext';
import BusinessLogic from '../../utils/BusinessLogic';
import './SurebetCalculator.css';

/**
 * SurebetCalculator - Main calculator component for arbitrage calculations
 * 
 * Features:
 * - Input fields for odds and total stake
 * - Real-time calculation updates on input changes
 * - Display calculated stakes for each bookmaker
 * - Show total profit and profit percentage
 * - Input validation and error messages
 * - Responsive design for mobile use
 * - Based on calculator.html mockup design
 */

const SurebetCalculator = ({
  initialOdds = [],
  initialStake = '',
  initialMarketType = '3-way',
  onCalculationComplete,
  className = '',
}) => {
  const { t } = useTranslation();
  const {
    inputs,
    calculations,
    validation,
    ui,
    actions,
  } = useCalculator();

  // Local state for form inputs
  const [localOdds, setLocalOdds] = useState(() => (
    initialOdds.length > 0
      ? initialOdds
      : [
        { bookmaker: 'Bookmaker 1', outcome: 'Outcome 1', value: '3.10' },
        { bookmaker: 'Bookmaker 2', outcome: 'Outcome 2', value: '3.40' },
        { bookmaker: 'Bookmaker 3', outcome: 'Outcome 3', value: '2.40' },
      ]
  ));
  const [localStake, setLocalStake] = useState(() => initialStake || '100');
  const [localMarketType, setLocalMarketType] = useState(() => initialMarketType || '3-way');
  const [activeEditIndex, setActiveEditIndex] = useState(null);

  // Sync total budget from context when fixed-stake mode recalculates it
  useEffect(() => {
    if (inputs.fixedMode === 'stake' && inputs.totalStake && inputs.totalStake !== localStake) {
      setLocalStake(inputs.totalStake);
    }
  }, [inputs.totalStake, inputs.fixedMode, localStake]);

  // Keep locked total in sync for anchored-total mode (derived legs still use anchored total)
  useEffect(() => {
    if (
      inputs.fixedMode !== 'totalAnchored'
      || inputs.anchoredTotal === null
    ) {
      return;
    }

    const manualCount = Object.keys(inputs.manualStakes || {}).length;
    const allManual = manualCount > 0 && manualCount === localOdds.length;

    if (allManual) {
      return;
    }

    if (String(inputs.anchoredTotal) !== localStake) {
      setLocalStake(String(inputs.anchoredTotal));
    }
  }, [inputs.anchoredTotal, inputs.fixedMode, inputs.manualStakes, localOdds.length, localStake]);

  // Sync local form state when calculator session changes (new match / reset)
  useEffect(() => {
    if (ui.modalSessionKey) {
      if (initialOdds.length > 0) {
        setLocalOdds(initialOdds);
      }
      setLocalStake(initialStake || '100');
      if (initialMarketType) {
        setLocalMarketType(initialMarketType);
      }
      setActiveEditIndex(null);
    }
  }, [ui.modalSessionKey, initialOdds, initialStake, initialMarketType]);

  // Initialize with props if provided (standalone usage without modal session)
  useEffect(() => {
    if (ui.modalSessionKey) {
      return;
    }

    if (initialOdds.length > 0) {
      setLocalOdds(initialOdds);
    }
    if (initialStake) {
      setLocalStake(initialStake);
    }
    if (initialMarketType) {
      setLocalMarketType(initialMarketType);
    }
  }, [initialOdds, initialStake, initialMarketType, ui.modalSessionKey]);

  // Helper function to determine market type from margin key
  const getMarketTypeFromMarginKey = (marginKey) => {
    // 2-way markets (Over/Under, Both Score, Goal/No Goal)
    const twoWayMarkets = [
      'marginOverUnder2AndHalf',
      'marginOverUnder1AndHalf', 
      'marginOverUnder3AndHalf',
      'marginBothScore',
      'margin1Xvs2',
      'marginX2vs1',
      'margin12vsX'
    ];
    
    // 3-way markets (classic 1X2 only; 3-way DC surebet is retired)
    const threeWayMarkets = [
      'margin1X2'
    ];
    
    if (twoWayMarkets.includes(marginKey)) {
      return '2-way';
    } else if (threeWayMarkets.includes(marginKey)) {
      return '3-way';
    } else {
      // Default fallback
      return '3-way';
    }
  };

  // Helper function to shorten outcome names for display
  const shortenOutcomeName = (outcome) => {
    if (!outcome) return t('surebets.calculator.outcome', {}, { defaultValue: 'Outcome' });
    
    // Map long outcome names to shorter, more readable versions
    const outcomeMap = {
      'CoefGoalUnder2AndHalf': 'Под 2.5',
      'CoefGoalOver2AndHalf': 'Над 2.5',
      'CoefGoalUnder1AndHalf': 'Под 1.5',
      'CoefGoalOver1AndHalf': 'Над 1.5',
      'CoefGoalUnder3AndHalf': 'Под 3.5',
      'CoefGoalOver3AndHalf': 'Над 3.5',
      'CoefBothScoreYes': t(
        'surebets.calculator.outcomeBothScoreYes',
        {},
        { defaultValue: 'Both Teams to Score' },
      ),
      'CoefBothScoreNo': t(
        'surebets.calculator.outcomeBothScoreNo',
        {},
        { defaultValue: 'Both Teams Not to Score' },
      ),
      'CoefYesGoal': 'Гол',
      'CoefNoGoal': 'Без гол',
      'Coef1': '1',
      'CoefX': 'X',
      'Coef2': '2',
      'Coef1X': '1X',
      'CoefX2': 'X2',
      'Coef12': '12'
    };
    
    return outcomeMap[outcome] || outcome;
  };

  // Get the actual market type from the margin key
  const actualMarketType = getMarketTypeFromMarginKey(localMarketType);
  
  // Get the original market type from the initial market type (for labels)
  const originalMarketType = getMarketTypeFromMarginKey(initialMarketType);

  // Update calculator context when local state changes
  useEffect(() => {
    actions.setTotalStake(localStake);
    actions.setOdds(localOdds);
    actions.setMarketType(localMarketType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localOdds, localStake, localMarketType]);

  // Real-time calculation on input changes - use context inputs directly
  useEffect(() => {
    const hasValidOdds = inputs.odds && inputs.odds.length > 0 && inputs.odds.every((odd) =>
      odd.value && !isNaN(parseFloat(odd.value)) && parseFloat(odd.value) > 1,
    );

    const hasValidStakeInput = inputs.fixedMode === 'totalAnchored'
      ? inputs.anchoredTotal !== null
        && !isNaN(parseFloat(inputs.anchoredTotal))
        && parseFloat(inputs.anchoredTotal) > 0
        && Object.keys(inputs.manualStakes || {}).length > 0
      : inputs.fixedMode === 'stake'
        ? inputs.fixedStakeValue
          && !isNaN(parseFloat(inputs.fixedStakeValue))
          && parseFloat(inputs.fixedStakeValue) > 0
        : inputs.totalStake
          && !isNaN(parseFloat(inputs.totalStake))
          && parseFloat(inputs.totalStake) > 0;

    if (hasValidStakeInput && hasValidOdds) {
      const timeoutId = setTimeout(() => {
        actions.calculateArbitrage();
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs, validation]);

  // Handle market type change
  const handleMarketTypeChange = (marketType) => {
    actions.clearFixedStake();
    setActiveEditIndex(null);
    setLocalMarketType(marketType);
    
    // If switching back to the original market type, restore original data
    if (marketType === initialMarketType) {
      // Restore original odds from props
      if (initialOdds && initialOdds.length > 0) {
        setLocalOdds(initialOdds);
      }
    } else {
      // Update odds based on market type for alternative options
      if (marketType === '2-way') {
        setLocalOdds([
          { bookmaker: 'Bookmaker 1', outcome: 'Over', value: '1.85' },
          { bookmaker: 'Bookmaker 2', outcome: 'Under', value: '2.05' },
        ]);
      } else if (marketType === '3-way') {
        setLocalOdds([
          { bookmaker: 'Bookmaker 1', outcome: 'Outcome 1', value: '3.10' },
          { bookmaker: 'Bookmaker 2', outcome: 'Outcome 2', value: '3.40' },
          { bookmaker: 'Bookmaker 3', outcome: 'Outcome 3', value: '2.40' },
        ]);
      }
    }
  };

  // Handle odds input change — reset fixed/anchored stakes so optimal split recalculates
  const handleOddsChange = (index, field, value) => {
    if (inputs.fixedMode !== 'total') {
      actions.clearFixedStake();
      setActiveEditIndex(null);
    }

    const newOdds = [...localOdds];
    newOdds[index] = { ...newOdds[index], [field]: value };
    setLocalOdds(newOdds);
  };

  // Handle total budget change
  const handleStakeChange = (value) => {
    actions.clearFixedStake();
    setActiveEditIndex(null);
    setLocalStake(value);
  };

  // Handle individual position stake change (locks position and recalculates others)
  const handleStakeDistributionChange = (index, value) => {
    if (inputs.fixedMode === 'totalAnchored') {
      actions.setManualStake(index, value);
      setActiveEditIndex(index);
      return;
    }

    actions.setFixedStake(index, value);
  };

  const isManualStakeIndex = (index) => (
    inputs.fixedMode === 'totalAnchored'
    && inputs.manualStakes
    && inputs.manualStakes[index] !== undefined
  );

  const isTotalAnchoredMode = inputs.fixedMode === 'totalAnchored';
  const isTotalBudgetLocked = isTotalAnchoredMode;

  const manualStakeCount = Object.keys(inputs.manualStakes || {}).length;
  const isAllPositionsManual = isTotalAnchoredMode
    && manualStakeCount > 0
    && manualStakeCount === localOdds.length;

  const displayedLockedTotal = useMemo(() => {
    if (!isTotalAnchoredMode) {
      return localStake;
    }

    if (isAllPositionsManual) {
      const actualTotal = Object.values(inputs.manualStakes).reduce((sum, value) => {
        const parsed = parseFloat(value);
        return sum + (isNaN(parsed) ? 0 : parsed);
      }, 0);

      return String(BusinessLogic.roundToPrecision(actualTotal, 2));
    }

    if (inputs.anchoredTotal !== null) {
      return String(inputs.anchoredTotal);
    }

    return localStake;
  }, [
    isTotalAnchoredMode,
    isAllPositionsManual,
    inputs.manualStakes,
    inputs.anchoredTotal,
    localStake,
  ]);

  const isStakeFieldEditable = (index) => (
    (inputs.fixedMode === 'stake'
      && (inputs.fixedStakeIndex === index || activeEditIndex === index))
    || isManualStakeIndex(index)
    || (isTotalAnchoredMode && activeEditIndex === index)
  );

  useEffect(() => {
    if (inputs.fixedMode === 'total') {
      setActiveEditIndex(null);
    }
  }, [inputs.fixedMode]);

  useEffect(() => {
    if (inputs.fixedMode === 'totalAnchored' && activeEditIndex !== null) {
      const isStillDerived = inputs.manualStakes?.[activeEditIndex] === undefined;
      if (!isStillDerived) {
        setActiveEditIndex(null);
      }
    }
  }, [inputs.fixedMode, inputs.manualStakes, activeEditIndex]);

  const previewFixedStakeResult = useMemo(() => {
    if (inputs.fixedMode !== 'stake' || inputs.fixedStakeIndex === null) {
      return null;
    }

    const fixedValue = parseFloat(inputs.fixedStakeValue);
    if (!inputs.fixedStakeValue || isNaN(fixedValue) || fixedValue <= 0) {
      return null;
    }

    const odds = localOdds.map((odd) => ({
      ...odd,
      value: parseFloat(odd.value),
    }));

    if (!odds.length || !odds.every((odd) => odd.value > 1)) {
      return null;
    }

    return BusinessLogic.calculateStakeDistributionFromFixedStake(
      odds,
      inputs.fixedStakeIndex,
      fixedValue,
    );
  }, [inputs.fixedMode, inputs.fixedStakeIndex, inputs.fixedStakeValue, localOdds]);

  const previewAnchoredTotalResult = useMemo(() => {
    if (inputs.fixedMode !== 'totalAnchored' || inputs.anchoredTotal === null) {
      return null;
    }

    const anchoredTotal = parseFloat(inputs.anchoredTotal);
    if (isNaN(anchoredTotal) || anchoredTotal <= 0) {
      return null;
    }

    const manualEntries = Object.entries(inputs.manualStakes || {});
    if (manualEntries.length === 0) {
      return null;
    }

    const odds = localOdds.map((odd) => ({
      ...odd,
      value: parseFloat(odd.value),
    }));

    if (!odds.length || !odds.every((odd) => odd.value > 1)) {
      return null;
    }

    return BusinessLogic.calculateStakeDistributionFromAnchoredTotal(
      odds,
      anchoredTotal,
      inputs.manualStakes,
    );
  }, [inputs.fixedMode, inputs.anchoredTotal, inputs.manualStakes, localOdds]);

  const getDistributionItemByIndex = (distribution, index) => {
    if (!distribution || distribution.length === 0) {
      return null;
    }

    return distribution.find((item) => item.index === index) || distribution[index] || null;
  };

  const handleReset = () => {
    if (ui.modalData) {
      actions.resetToInitialSession();
    } else {
      actions.clearFixedStake();
      actions.setTotalStake(initialStake || '100');
      if (initialOdds.length > 0) {
        actions.setOdds(initialOdds);
      }
      actions.setMarketType(initialMarketType);
    }

    setLocalStake(initialStake || '100');
    setActiveEditIndex(null);
    if (initialOdds.length > 0) {
      setLocalOdds(initialOdds);
    }
    if (initialMarketType) {
      setLocalMarketType(initialMarketType);
    }
  };

  const handleStakeFieldFocus = (index) => {
    if (isStakeFieldEditable(index)) {
      return;
    }

    if (isTotalAnchoredMode && !isManualStakeIndex(index)) {
      setActiveEditIndex(index);
      return;
    }

    if (inputs.fixedMode === 'stake' && inputs.fixedStakeIndex !== null) {
      if (index !== inputs.fixedStakeIndex) {
        setActiveEditIndex(index);
      }
      return;
    }

    const calculatedItem = getDistributionItemByIndex(calculations.stakeDistribution, index);
    const currentValue = calculatedItem?.stake;

    if (currentValue > 0) {
      actions.setFixedStake(index, String(currentValue));
      setActiveEditIndex(index);
    }
  };

  const getStakeInputValue = (index) => {
    if (isManualStakeIndex(index)) {
      return inputs.manualStakes[index] ?? '';
    }

    if (inputs.fixedMode === 'stake' && inputs.fixedStakeIndex === index) {
      return inputs.fixedStakeValue ?? '';
    }

    const previewItem = getDistributionItemByIndex(
      previewAnchoredTotalResult?.distribution || previewFixedStakeResult?.distribution,
      index,
    );
    if (previewItem) {
      return previewItem.stake.toFixed(2);
    }

    const calculatedItem = getDistributionItemByIndex(calculations.stakeDistribution, index);
    return calculatedItem ? calculatedItem.stake.toFixed(2) : '';
  };

  const getPositionProfitValue = (index) => {
    const previewItem = getDistributionItemByIndex(
      previewAnchoredTotalResult?.distribution || previewFixedStakeResult?.distribution,
      index,
    );
    if (previewItem && typeof previewItem.profit === 'number') {
      return previewItem.profit;
    }

    const profit = calculations.profitPerPosition?.[index];
    return typeof profit === 'number' ? profit : null;
  };

  const getStakeInputClassName = (index) => {
    if (isStakeFieldEditable(index)) {
      return 'form-input stake-input stake-input--editable stake-input--fixed';
    }

    if (isTotalAnchoredMode) {
      return 'form-input stake-input stake-input--derived stake-input--anchored-derived readonly';
    }

    return 'form-input stake-input stake-input--derived readonly';
  };

  const getStakeFieldHint = (index) => {
    if (isManualStakeIndex(index)) {
      return t(
        'surebets.calculator.stakeFieldAnchoredHint',
        {},
        { defaultValue: 'Manual position — total budget is locked' },
      );
    }

    if (isStakeFieldEditable(index)) {
      return t(
        'surebets.calculator.stakeFieldEditableHint',
        {},
        { defaultValue: 'Fixed position — other amounts recalculate automatically' },
      );
    }

    if (isTotalAnchoredMode && !isManualStakeIndex(index)) {
      return t(
        'surebets.calculator.stakeFieldPromoteHint',
        {},
        { defaultValue: 'Click to set this position manually' },
      );
    }

    return t(
      'surebets.calculator.stakeFieldReadOnlyHint',
      {},
      { defaultValue: 'Click to fix this position amount' },
    );
  };

  const getPositionProfitStyling = (profit) => {
    if (profit > 0) {
      return 'stake-profit-hint--positive';
    }
    if (profit < 0) {
      return 'stake-profit-hint--negative';
    }
    return 'stake-profit-hint--neutral';
  };

  // Get profit display styling
  const getProfitStyling = () => {
    if (calculations.guaranteedProfit > 0) {
      return {
        className: 'profit-positive',
        badgeClass: 'profit-badge-positive',
        icon: '↗',
      };
    } else if (calculations.guaranteedProfit < 0) {
      return {
        className: 'profit-negative',
        badgeClass: 'profit-badge-negative',
        icon: '↘',
      };
    } else {
      return {
        className: 'profit-neutral',
        badgeClass: 'profit-badge-neutral',
        icon: '→',
      };
    }
  };

  const profitStyling = getProfitStyling();

  return (
    <div className={`surebet-calculator ${className}`}>
      <div className="calculator-container">
        <h1 className="calculator-title">
          {t('surebets.calculator.title', {}, { defaultValue: 'Arbitrage Calculator' })}
        </h1>
        
        <div className="calculator-form">
          {/* Market Type Selection */}
          <div className="form-group">
            <label className="form-label">
              {t('surebets.calculator.marketType', {}, { defaultValue: 'Market Type' })}
            </label>
            <select
              className="form-select"
              value={localMarketType}
              onChange={(e) => handleMarketTypeChange(e.target.value)}
            >
              {/* Current surebet's market type as first option */}
              <option value={initialMarketType}>
                {originalMarketType === '2-way'
                  ? t(
                      'surebets.calculator.market2WayDetailed',
                      {},
                      { defaultValue: '2-way (e.g. Both Teams to Score, Over/Under)' },
                    )
                  : t(
                      'surebets.calculator.market3WayDetailed',
                      {},
                      { defaultValue: '3-way (1X2 Match Result)' },
                    )}
              </option>
              {/* Alternative market type */}
              <option value={originalMarketType === '2-way' ? '3-way' : '2-way'}>
                {originalMarketType === '2-way'
                  ? t(
                      'surebets.calculator.market3WayDetailed',
                      {},
                      { defaultValue: '3-way (1X2 Match Result)' },
                    )
                  : t(
                      'surebets.calculator.market2WayDetailed',
                      {},
                      { defaultValue: '2-way (e.g. Both Teams to Score, Over/Under)' },
                    )}
              </option>
            </select>
          </div>

          {/* Odds Input Fields */}
          <div className="odds-inputs">
            {localOdds.map((odd, index) => (
              <div key={index} className="form-group">
                <label className="form-label">
                  {`${t('surebets.calculator.coefficient', {}, { defaultValue: 'Coefficient' })} ${
                    odd.outcome
                      || `${t('surebets.calculator.outcome', {}, { defaultValue: 'Outcome' })} ${index + 1}`
                  }`}
                </label>
                <input
                  type="number"
                  className="form-input"
                  placeholder="Odds"
                  value={odd.value}
                  onChange={(e) => handleOddsChange(index, 'value', e.target.value)}
                  step="0.01"
                  min="1.01"
                />
                {validation.errors[`odds_${index}`] && (
                  <div className="error-message">
                    {validation.errors[`odds_${index}`]}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Total Stake Input */}
          <div className="form-group">
            <label className="form-label">
              {t('surebets.calculator.totalStake', {}, { defaultValue: 'Total budget for allocation' })}
            </label>
            <input
              type="number"
              className={`form-input${isTotalBudgetLocked ? ' total-budget-input--locked readonly' : ''}`}
              placeholder={t('surebets.calculator.totalStakePlaceholder', {}, { defaultValue: 'Enter total budget' })}
              value={isTotalBudgetLocked ? displayedLockedTotal : localStake}
              onChange={isTotalBudgetLocked ? undefined : (e) => handleStakeChange(e.target.value)}
              readOnly={isTotalBudgetLocked}
              step="0.01"
              min="0"
              data-testid="total-budget-input"
              aria-readonly={isTotalBudgetLocked}
            />
            {isTotalBudgetLocked && (
              <div className="stake-field-hint">
                {t(
                  'surebets.calculator.totalBudgetLockedHint',
                  {},
                  { defaultValue: 'Total budget is locked while manual positions are set' },
                )}
              </div>
            )}
            {validation.errors.totalStake && (
              <div className="error-message">
                {validation.errors.totalStake}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="calculator-divider"></div>

          {/* Calculated Results */}
          <h2 className="results-title">
            {t('surebets.calculator.calculatedStakes', {}, { defaultValue: 'Optimal allocation' })}
          </h2>

          {/* Stake Distribution */}
          <div className="stake-outputs">
            {(calculations.stakeDistribution.length > 0
              ? calculations.stakeDistribution
              : localOdds.map((odd, index) => ({
                bookmaker: odd.bookmaker,
                outcome: odd.outcome,
                stake: 0,
              }))
            ).map((stake, index) => {
              const positionProfit = getPositionProfitValue(index);
              const isEditableStake = isStakeFieldEditable(index);

              return (
                <div key={index} className="form-group stake-output-group">
                  <label className="form-label">
                    {`${t('surebets.calculator.stakeFor', {}, { defaultValue: 'Amount for position' })} "${shortenOutcomeName(stake.outcome)}" (EUR)`}
                  </label>
                  <input
                    type="number"
                    className={getStakeInputClassName(index)}
                    value={getStakeInputValue(index)}
                    readOnly={!isEditableStake}
                    onFocus={() => handleStakeFieldFocus(index)}
                    onChange={isEditableStake
                      ? (e) => handleStakeDistributionChange(index, e.target.value)
                      : undefined}
                    step="0.01"
                    min="0"
                    data-testid={`stake-input-${index}`}
                    aria-readonly={!isEditableStake}
                  />
                  {validation.errors[`stake_${index}`] && (
                    <div className="error-message">
                      {validation.errors[`stake_${index}`]}
                    </div>
                  )}
                  <div className="stake-field-hint">
                    {getStakeFieldHint(index)}
                  </div>
                  {positionProfit !== null && (
                    <div className={`stake-profit-hint ${getPositionProfitStyling(positionProfit)}`}>
                      {t(
                        'surebets.calculator.positionProfit',
                        { outcome: shortenOutcomeName(stake.outcome), amount: positionProfit.toFixed(2) },
                        { defaultValue: 'Expected return (position "{outcome}"): {amount} EUR' },
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Overall ROI */}
          <div className="form-group">
            <label className="form-label">
              {calculations.guaranteedProfit < 0
                ? t('surebets.calculator.expectedResult', {}, { defaultValue: 'Expected result' })
                : t('surebets.calculator.overallRoi', {}, { defaultValue: 'Overall expected return (ROI)' })}
            </label>
            <div className="profit-container profit-container--percent-only">
              <span className={`profit-badge profit-badge--overall ${profitStyling.badgeClass}`}>
                {profitStyling.icon} {calculations.profitPercentage.toFixed(2)}%
              </span>
            </div>
          </div>

          <div className="calculator-actions">
            <button
              type="button"
              className="calculator-reset-button"
              onClick={handleReset}
              data-testid="calculator-reset-button"
            >
              {t('surebets.calculator.reset', {}, { defaultValue: 'Reset' })}
            </button>
          </div>

          {/* Arbitrage Status */}


        </div>
      </div>
    </div>
  );
};

SurebetCalculator.propTypes = {
  initialOdds: PropTypes.arrayOf(PropTypes.shape({
    bookmaker: PropTypes.string,
    outcome: PropTypes.string,
    value: PropTypes.string,
  })),
  initialStake: PropTypes.string,
  initialMarketType: PropTypes.string,
  onCalculationComplete: PropTypes.func,
  className: PropTypes.string,
};

export default SurebetCalculator;
